// LightReviewPanel.jsx: super admin review queue for ArguMentor Light (SupportDashboard).
// Blocked messages can be released when they only looked like a threat; reports can end
// a conversation; everything can be dismissed. The server allows this for support only.
import React, { useCallback, useEffect, useState } from 'react';
import { ShieldAlert, RefreshCw } from 'lucide-react';
import { light } from './lightClient.js';

const TYPE_LABEL = { block: 'Blocked message', crisis: 'Crisis signal', report: 'Reported message', language: 'Held: not English', manipulation: 'Aimed at the screener' };

function LightReviewPanel() {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try { setEntries((await light('reviewList')).entries); }
    catch (e) { setError(e.message); setEntries([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (reviewId, decision) => {
    setBusy(reviewId + decision);
    try { await light('reviewResolve', { reviewId, decision }); setEntries(prev => prev.filter(e => e.id !== reviewId)); }
    catch (e) { setError(e.message); }
    setBusy(null);
  };

  const nameOf = (entry, userId) => entry.conversation?.participants?.[userId]?.name || userId;

  return (
    <section className="bg-white border border-stone-200 rounded-xl p-5" aria-labelledby="light-review-heading">
      <div className="flex items-center gap-2 mb-3">
        <ShieldAlert className="w-4 h-4 text-amber-700" aria-hidden="true" />
        <h2 id="light-review-heading" className="text-sm font-semibold text-stone-800 flex-1">
          Say It Together review queue {entries && <span className="text-stone-600 font-normal">({entries.length} open)</span>}
        </h2>
        <button onClick={load} className="p-1.5 text-stone-600 hover:text-stone-800" aria-label="Refresh review queue"><RefreshCw className="w-4 h-4" /></button>
      </div>
      {error && <p role="alert" className="text-sm text-rose-800 mb-2">{error}</p>}
      {entries === null && <p className="text-sm text-stone-600">Loading…</p>}
      {entries?.length === 0 && <p className="text-sm text-stone-600">Nothing waiting for review.</p>}
      <ul className="space-y-3">
        {entries?.map(entry => (
          <li key={entry.id} className="border border-stone-200 rounded-lg p-3 text-sm">
            <p className="text-xs text-stone-600 mb-1">
              <span className="font-semibold text-stone-800">{TYPE_LABEL[entry.type] || entry.type}</span>
              {' · '}{new Date(entry.createdAt).toLocaleString()}
              {entry.conversation && <> · “{entry.conversation.topic}” ({entry.conversation.status}{entry.conversation.endedReason ? `, ${entry.conversation.endedReason}` : ''})</>}
            </p>
            <p className="text-stone-900 mb-1">“{entry.text}”</p>
            <p className="text-xs text-stone-600 mb-2">
              From {nameOf(entry, entry.senderId)}
              {entry.reporterId && <>, reported by {nameOf(entry, entry.reporterId)}{entry.reason ? `: ${entry.reason}` : ''}</>}
              {entry.categories && <> · {entry.categories.join(', ')}</>}
              {entry.type !== 'report' && entry.reason && <> · {entry.reason}</>}
            </p>
            <div className="flex flex-wrap gap-2">
              {['block', 'language', 'manipulation'].includes(entry.type) && (
                <button onClick={() => resolve(entry.id, 'release')} disabled={Boolean(busy)} className="px-3 py-1.5 rounded-lg bg-emerald-700 text-white disabled:bg-stone-300">Release to conversation</button>
              )}
              {entry.conversation && entry.conversation.status !== 'ended' && (
                <button onClick={() => resolve(entry.id, 'end')} disabled={Boolean(busy)} className="px-3 py-1.5 rounded-lg bg-rose-700 text-white disabled:bg-stone-300">End conversation</button>
              )}
              <button onClick={() => resolve(entry.id, 'dismiss')} disabled={Boolean(busy)} className="px-3 py-1.5 rounded-lg border border-stone-300 text-stone-700">{entry.type === 'block' ? 'Keep blocked' : 'Dismiss'}</button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default LightReviewPanel;
